import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import ReactSiema, {
  type ReactSiemaImperativeHandle,
  type ReactSiemaProps
} from "./lib";

export type ReactSiemaCarouselProps = React.PropsWithChildren<
  {
    controls?: boolean;
  } & Omit<ReactSiemaProps, "children" | "updateAfterDrag" | "onResize">
>;

export default function ReactSiemaCarousel({
  children,
  controls,
  ...rest
}: ReactSiemaCarouselProps) {
  const sliderRef = useRef<ReactSiemaImperativeHandle | null>(null);
  const total = React.Children.count(children);
  const [perPage, setPerPage] = useState(1);
  const [current, setCurrent] = useState(0);

  const getSlider = useCallback(() => sliderRef.current, []);

  useEffect(() => {
    const slider = getSlider();
    if (!slider) return;
    setCurrent(slider.currentSlide);
    setPerPage(slider.getPerPage());
  }, [getSlider]);

  const updateAfterDrag = useCallback(() => {
    const slider = getSlider();
    if (!slider) return;
    setCurrent(slider.currentSlide);
  }, [getSlider]);

  const gotoNext = useCallback(() => {
    const slider = getSlider();
    if (!slider) return;
    slider.next();
    setCurrent(slider.currentSlide);
  }, [getSlider]);

  const gotoPrev = useCallback(() => {
    const slider = getSlider();
    if (!slider) return;
    slider.prev();
    setCurrent(slider.currentSlide);
  }, [getSlider]);

  const onDotClick = useCallback(
    (dotIndex: number) => {
      let nextCurrent = dotIndex * perPage;
      const slider = getSlider();
      if (perPage + nextCurrent > total) {
        nextCurrent = total - perPage;
      }
      setCurrent(nextCurrent);
      if (slider) slider.goTo(nextCurrent);
    },
    [getSlider, perPage, total]
  );

  const handleResize = useCallback((nextPerPage: number) => {
    setPerPage(nextPerPage);
  }, []);

  const dotsContent = useMemo(() => {
    if (perPage === 0) {
      return null;
    }
    const dotCount = Math.ceil(total / perPage);
    let dotIndex = Math.floor(current / perPage);
    if (current + perPage >= total) {
      dotIndex = dotCount - 1;
    }

    if (dotCount <= 1) {
      return null;
    }

    const dots: React.ReactElement[] = [];
    for (let i = 0; i < dotCount; i += 1) {
      const active = i === dotIndex ? "active" : "";
      dots.push(
        <button
          key={i}
          type="button"
          onClick={() => onDotClick(i)}
          className={`slider-dot ${active}`}
        />
      );
    }
    return dots;
  }, [current, onDotClick, perPage, total]);

  return (
    <Fragment>
      <ReactSiema
        ref={sliderRef}
        onResize={handleResize}
        updateAfterDrag={updateAfterDrag}
        {...rest}
      >
        {children}
      </ReactSiema>
      {controls !== false && (
        <div className="slider-nav text-center">
          <button
            type="button"
            className="left-arrow btn btn-link"
            onClick={gotoPrev}
          />
          <div className="slider-dot-container">{dotsContent}</div>
          <button
            type="button"
            className="left-arrow btn btn-link"
            onClick={gotoNext}
          />
        </div>
      )}
    </Fragment>
  );
}
